// @flow

import React, { Children, Component, Fragment, type Node } from 'react';
import classnames from 'classnames';

import responsiveness from 'Components/utils/responsive/Responsive.jsx';

import {
  MoreResults,
  type MoreResultsProps,
} from 'Components/frameworks/more-results/MoreResults.jsx';

function getWidth(fraction: number = 1): string {
  return fraction < 2 || fraction > 6 ? 'w-full' : `w-1/${fraction}`;
}

function filterContext(hasOneRemaining: boolean, itemsByLine: number, isSeeMoreVisible: boolean) {
  return (item: *, index: number) => {
    if (isSeeMoreVisible) {
      return index < itemsByLine;
    }
    return true;
  };
}

type GridProps = {
  children: Node,
  className?: string,
  display?: ScreenSizesProps,
  getChildren?: () => *,
  grid?: ScreenSizesProps,
  hasSeeMore?: boolean,
  isLoading?: boolean,
  isSynchronousGrid?: boolean,
  responsive?: ResponsiveProps,
  seeMore?: MoreResultsProps,
  totalNumberToDisplay?: number,
};

type State = {
  isExpanded: boolean,
};

export class Grid extends Component<GridProps, State> {
  static defaultProps = {
    display: {
      mobile: 1,
      phablet: 2,
      tablet: 3,
      desktop: 3,
    },
    getChildren: () => Promise.resolve({}),
    grid: {
      mobile: 1,
      phablet: 2,
      tablet: 3,
      desktop: 3,
    },
  };

  state: State = {
    isExpanded: !this.props.hasSeeMore,
  };

  getGrid() {
    const { grid = {} } = this.props;
    const { desktop, tablet, phablet, mobile } = grid;

    return `${getWidth(mobile)} sm:${getWidth(phablet)} md:${getWidth(tablet)} lg:${getWidth(
      desktop,
    )}`;
  }

  getItemsByLine(): number {
    const { display = {}, responsive = {} } = this.props;
    const { desktop = 1, tablet = 1, phablet = 1, mobile = 1 } = display;

    const { isBrowserGreaterThan = {} } = responsive;

    if (isBrowserGreaterThan.M && desktop > -1) {
      return desktop;
    }

    if (isBrowserGreaterThan.S && tablet > -1) {
      return tablet;
    }

    if (isBrowserGreaterThan.XS && phablet > -1) {
      return phablet;
    }

    return mobile;
  }

  getNumberOfItemsByLineExcludingSeeMore(): number {
    return this.getItemsByLine();
  }

  getRest() {
    const { children, totalNumberToDisplay } = this.props;

    const nbItemsByLineExcludingSeeMore = this.getNumberOfItemsByLineExcludingSeeMore();
    return totalNumberToDisplay
      ? totalNumberToDisplay - nbItemsByLineExcludingSeeMore
      : Children.count(children) - nbItemsByLineExcludingSeeMore;
  }

  onClickSeeMore = () => {
    const {
      totalNumberToDisplay = 0,
      grid = {},
      getChildren = () => Promise.resolve({}),
      isSynchronousGrid = false,
    } = this.props;

    if (isSynchronousGrid || totalNumberToDisplay === grid.desktop) {
      this.setState({ isExpanded: true });
    }
    if (totalNumberToDisplay > grid.desktop) {
      getChildren();
    }
  };

  getSeeMore() {
    const { hasSeeMore, isLoading, seeMore } = this.props;

    return hasSeeMore && this.getRest() > 0 && !this.state.isExpanded ? (
      <MoreResults
        {...seeMore}
        className="h-full"
        isLoading={isLoading}
        number={this.getRest()}
        onClick={this.onClickSeeMore}
        grid={this.getGrid()}
        maxPlaceholders={this.getItemsByLine()}
      />
    ) : null;
  }

  getChildren(): Array<*> {
    const hasOneRemaining = this.getRest() === 1;
    const filterItem = filterContext(
      hasOneRemaining,
      this.getNumberOfItemsByLineExcludingSeeMore(),
      !this.state.isExpanded,
    );
    return Children.toArray(this.props.children)
      .filter(filterItem)
      .map(item => {
        return (
          <div
            className={classnames('fadeIn p-2 pb-3', this.getGrid())}
            data-testid="Grid-item"
            key={item.key}
          >
            {item}
          </div>
        );
      });
  }

  getItems(): Array<?Node> {
    return [...this.getChildren(), this.getSeeMore()].filter(i => i);
  }

  componentDidUpdate({ isLoading }: GridProps) {
    if (isLoading && !this.props.isLoading && !this.state.isExpanded) {
      this.setState({ isExpanded: true });
    }
  }

  render() {
    const { className } = this.props;

    const items = this.getItems();

    return items.length ? (
      <div className={classnames(className, 'flex flex-wrap -m-2')}>
        {items.map((item, index) => (
          /* eslint-disable react/no-array-index-key */
          <Fragment key={index}>{item}</Fragment>
        ))}
      </div>
    ) : null;
  }
}

export default responsiveness(Grid);
